import { AnyAction, Reducer, ActionCreator } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { RootState } from './getRootModule';
import { debounce } from '@Utils/facade/lodash';
import { globalsSelector } from '@Utils/facade/edgeTs';

export enum SIZE {
  SERVER = 'initial',
  MOBILE = 'xs',
  TABLET = 'sm',
  DESKTOP = 'md',
}

const MEDIA_QUERIES = [
  {
    name: SIZE.MOBILE,
    media: '(max-width: 767px)',
  },
  {
    name: SIZE.TABLET,
    media: '(min-width: 768px) and (max-width: 1023px)',
  },
  {
    name: SIZE.DESKTOP,
    media: '(min-width: 1024px)',
  },
];

export const MEDIA_MATCHED_UPDATE = 'media.matched.updateV3';

const SERVER_RESOLUTION_TYPE = {
  Mobile: SIZE.MOBILE,
  Tablet: SIZE.TABLET,
  Desktop: SIZE.DESKTOP,
};

export interface MediaQueriesState {
  currentSize: SIZE;
}

const initialState = {
  currentSize: SIZE.SERVER,
};

export const reducer: Reducer<MediaQueriesState, AnyAction> = (
  state = initialState,
  action = {
    type: null,
  },
) => {
  switch (action.type) {
    case MEDIA_MATCHED_UPDATE:
      return {
        ...state,
        currentSize: action.payload,
      };
    default:
      return state;
  }
};

const storage = [];

const mediaMatchAction = (payload: string) => ({
  type: MEDIA_MATCHED_UPDATE,
  payload,
});

export const initMediaQueries: ActionCreator<ThunkAction<void, RootState, void, AnyAction>> =
  () => (dispatch) => {
    if (storage.length <= 0) {
      MEDIA_QUERIES.forEach((query) => {
        const mediaQueryList: MediaQueryList = window.matchMedia(query.media);
        const mediaQueryListListener: (ev: MediaQueryListEvent | MediaQueryList) => any = debounce(
          (mq: MediaQueryListEvent | MediaQueryList) => {
            if (mq.matches) {
              dispatch(mediaMatchAction(query.name));
            }
          },
          500,
        );

        mediaQueryList.addListener(mediaQueryListListener);

        if (mediaQueryList.matches) {
          mediaQueryListListener(mediaQueryList);
        }

        storage.push({
          mediaQueryList,
          mediaQueryListListener,
        });
      });
    }
  };

type ResolutionTypeKeys = keyof typeof SERVER_RESOLUTION_TYPE;

const serverResolutionTypeSelector = (state: any): ResolutionTypeKeys => {
  const globals = globalsSelector(state);
  return globals && globals.resolutionType
    ? (globals.resolutionType as ResolutionTypeKeys)
    : 'Desktop';
};

export const currentSizeNameSelector: (state: RootState) => SIZE = (state: RootState) =>
  state.mediaQueriesV3.currentSize === SIZE.SERVER
    ? SERVER_RESOLUTION_TYPE[serverResolutionTypeSelector(state)]
    : state.mediaQueriesV3.currentSize;

export type BooleanSelector = (state: RootState) => boolean;

const SMALL_DEVICES_SIZES = [SIZE.MOBILE, SIZE.TABLET];
const DESKTOP_SIZES = [SIZE.DESKTOP];

export const isMobileDevicesSelector: BooleanSelector = (state) =>
  SMALL_DEVICES_SIZES.includes(currentSizeNameSelector(state));

export const isDesktopDeviceSelector = (state: RootState) =>
  DESKTOP_SIZES.includes(currentSizeNameSelector(state));
